import type { ActionSummary, Workspace } from "../api/contract";
import type { SectionSummary } from "../components/PreviewFrame";

/** Whole versions: they stay under "Earlier versions" and have no per-change Undo. */
const WHOLE_VERSIONS = ["bundle.bootstrap", "draft.restore"];
/**
 * Blog changes made before the blog had its own storage. They stay in the action log, but the blog is no longer part of the Website's
 * versions: it isn't counted, listed or discarded here, and it keeps its own history.
 */
export const LEGACY_BLOG_KINDS = ["module.configure", "post.create", "post.update", "post.delete", "tag.create", "tag.update", "tag.delete"];
/** Actions that go live or offline rather than change the draft. */
const PUBLISHING = ["publish", "rollback", "unpublish"];

export interface ChangeRow {
  action: ActionSummary;
  /** Its number in the list and on its marker, 1 for the newest. */
  number: number;
  /** Taken back by a later undo that hasn't itself been undone. */
  undone: boolean;
  /** What the row's button submits as `change.undo`: the change itself, or the undo to take back (Redo). Null when it can't be undone. */
  target: string | null;
}

/**
 * The draft changes made since the live version, newest first. An undo folds into the row of the change it
 * took back (which then offers Redo), so undoing and redoing never adds rows.
 */
export function unpublishedChanges(workspace: Workspace): ChangeRow[] {
  const drafts = workspace.actions.filter((action) => action.state === "succeeded" && typeof action.result?.draftId === "string");
  const liveAt = workspace.liveDraftId ? drafts.findIndex((action) => action.result?.draftId === workspace.liveDraftId) : -1;
  const range = (liveAt >= 0 ? drafts.slice(0, liveAt) : drafts).filter((action) => !PUBLISHING.includes(action.kind) && !LEGACY_BLOG_KINDS.includes(action.kind));
  const ids = new Set(range.map((action) => action.id));
  // Newest first, so the first undo seen for an action is its latest one.
  const latestUndo = new Map<string, ActionSummary>();
  for (const action of range) if (action.kind === "change.undo" && action.undoes && ids.has(action.undoes) && !latestUndo.has(action.undoes)) latestUndo.set(action.undoes, action);
  const folded = (action: ActionSummary) => action.kind === "change.undo" && !!action.undoes && ids.has(action.undoes);
  // An undo whose change is older than the live version is a change in its own right.
  return range.filter((action) => !folded(action)).map((action, index) => {
    // Undo, redo, undo…: an odd chain means the change is taken back. The button acts on the chain's newest step.
    const chain: ActionSummary[] = [];
    for (let next = latestUndo.get(action.id); next; next = latestUndo.get(next.id)) chain.push(next);
    const newest = chain.at(-1)?.id ?? action.id;
    return { action, number: index + 1, undone: chain.length % 2 === 1, target: WHOLE_VERSIONS.includes(action.kind) ? null : newest };
  });
}

/** The records and hidden sections a change touched, as far as the list can tell. */
function touched(action: ActionSummary, file: string | undefined): { records: Set<string>; nodes: Set<string> } {
  const records = new Set(action.changes?.records ?? (action.subject ? [action.subject.recordId] : []));
  const nodes = new Set((action.changes?.hiddenNodes ?? []).filter((entry) => entry.file === file).map((entry) => entry.nodeId));
  return { records, nodes };
}

/** The sections on the shown page that show a change. The bridge reports the record each slot shows, so this holds after a reorder. */
export function changeSections(row: ChangeRow, sections: SectionSummary[], file: string | undefined): SectionSummary[] {
  if (row.undone) return [];
  const { records, nodes } = touched(row.action, file);
  return sections.filter((section) => nodes.has(section.nodeId) || section.binds.some((bind) => records.has(bind.split(".")[0]!)));
}

/** One numbered marker per section; a section with several changes shows the newest one's number. */
export function changeMarkers(rows: ChangeRow[], sections: SectionSummary[], file: string | undefined): Array<{ nodeId: string; number: number; actionId: string }> {
  const markers = new Map<string, { nodeId: string; number: number; actionId: string }>();
  for (const row of rows) for (const section of changeSections(row, sections, file)) {
    if (!markers.has(section.nodeId)) markers.set(section.nodeId, { nodeId: section.nodeId, number: row.number, actionId: row.action.id });
  }
  return [...markers.values()];
}

/** "3 changes not live", or null when the draft has none. */
export function unpublishedLabel(rows: ChangeRow[]): string | null {
  const count = rows.filter((row) => !row.undone).length;
  return count ? `${count} ${count === 1 ? "change" : "changes"} not live` : null;
}
